/**
 * ET - Trustpilot live data. Loaded once from snippets/et-base-head.liquid when "ET - Trustpilot > Load live
 * Trustpilot data" is on.
 *
 * Source: the public TrustBox feed (widget.trustpilot.com/trustbox-data/<template>) that Trustpilot's own TrustBox
 * widgets read. It needs no API key, answers any origin (CORS *) and is cached for 30 minutes by Trustpilot's CDN
 * and by the browser, so every page after the first reads it from cache.
 *
 * What it does:
 * - Fills every [data-et-tp] placeholder written by snippets/et-trustpilot-text.liquid:
 *   score (TrustScore, 4,4) · stars (star rating, 4,5) · reviews (review count, 2.829) · label (Uitstekend).
 * - Rewrites the aria-label of every [data-et-tp-label] element (its placeholder text) with the live values.
 * - Redraws the rating boxes (snippets/et-cart-page-rating-boxes.liquid) marked [data-et-tp-stars] to the live
 *   star rating, when the script tag carries data-live-stars="true".
 * - Exposes window.etTrustpilot.reviews(options) and .drawBoxes(svg, rating) for sections that render live
 *   reviews (assets/et-reviews-carousel.js).
 * The server-rendered fallback values (theme settings) stay in place when the feed cannot be reached.
 */
(() => {
  if (window.etTrustpilot) return;

  const script = document.currentScript;
  const config = script ? script.dataset : {};
  const BUSINESS_UNIT_ID = (config.businessUnit || '').trim();
  if (!BUSINESS_UNIT_ID) return;

  const FEED_URL = 'https://widget.trustpilot.com/trustbox-data/';
  // Micro Star TrustBox: business unit summary only, the smallest response.
  const SUMMARY_TEMPLATE = '5419b6a8b0d04a076446a9ad';
  // Grid TrustBox, the one finexhome.com already embeds: summary plus reviews.
  const REVIEWS_TEMPLATE = '54ad5defc6454f065c28af8b';
  const REQUEST_TIMEOUT = 8000;
  const MAX_REVIEWS = 30;

  // Trustpilot wants a full locale; the storefront only knows its language. Unlisted languages fall back to en-GB.
  const TRUSTPILOT_LOCALES = {
    da: 'da-DK',
    de: 'de-DE',
    en: 'en-GB',
    es: 'es-ES',
    fi: 'fi-FI',
    fr: 'fr-FR',
    it: 'it-IT',
    ja: 'ja-JP',
    nb: 'nb-NO',
    nl: 'nl-NL',
    pl: 'pl-PL',
    pt: 'pt-PT',
    sv: 'sv-SE'
  };

  const LANGUAGE = (config.language || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
  const LOCALE = TRUSTPILOT_LOCALES[LANGUAGE] || 'en-GB';
  const LIVE_STARS = config.liveStars === 'true';

  const FILLED_CLASS = 'et-cart-page-rating-boxes__box--filled';
  const STAR_CLASS = 'et-cart-page-rating-boxes__star';

  const requests = new Map();
  let reviewsRequest = null;

  const request = (template, params) => {
    const query = new URLSearchParams({ businessUnitId: BUSINESS_UNIT_ID, locale: LOCALE, ...params });
    const url = `${FEED_URL}${template}?${query}`;
    if (requests.has(url)) return requests.get(url);

    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT) : null;
    const pending = fetch(url, { credentials: 'omit', signal: controller ? controller.signal : undefined })
      .then((response) => {
        if (!response.ok) throw new Error(`Trustpilot feed ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (!data || !data.businessUnit) throw new Error('Trustpilot feed without business unit');
        return data;
      })
      .finally(() => clearTimeout(timer));

    requests.set(url, pending);
    return pending;
  };

  /* Any request carries the business unit, so the summary reuses a reviews request already on its way (and asks for
     the summary on its own only when that one fails). */
  const summary = () =>
    (reviewsRequest
      ? reviewsRequest.catch(() => request(SUMMARY_TEMPLATE, {}))
      : request(SUMMARY_TEMPLATE, {})
    ).then((data) => {
      const unit = data.businessUnit;
      const counts = unit.numberOfReviews || {};
      return {
        score: Number(unit.trustScore),
        stars: Number(unit.stars),
        reviews: Number(counts.total),
        label: data.starsString || '',
        profileUrl: (data.links && data.links.profileUrl) || ''
      };
    });

  /**
   * @param {object} [options]
   * @param {string} [options.stars] - Comma list of star values to include, e.g. '4,5' (default '5')
   * @param {string} [options.languages] - Review language, e.g. 'nl', or 'all' (default: the storefront language)
   * @param {number} [options.count] - Number of reviews, 1 to 30 (default 12)
   * @returns {Promise<Array<{stars: number, title: string, text: string, name: string, date: Date|null, url: string}>>}
   */
  const reviews = (options = {}) => {
    const count = Math.min(MAX_REVIEWS, Math.max(1, parseInt(options.count, 10) || 12));
    const pending = request(REVIEWS_TEMPLATE, {
      reviewLanguages: options.languages || LANGUAGE,
      reviewStars: options.stars || '5',
      reviewsPerPage: String(count)
    });
    if (!reviewsRequest) reviewsRequest = pending;

    return pending.then((data) =>
      (Array.isArray(data.reviews) ? data.reviews : []).slice(0, count).map((review) => {
        const date = review.createdAt ? new Date(review.createdAt) : null;
        return {
          stars: Number(review.stars) || 0,
          title: review.title || '',
          text: review.text || '',
          name: (review.consumer && review.consumer.displayName) || '',
          date: date && !Number.isNaN(date.getTime()) ? date : null,
          url: review.reviewUrl || ''
        };
      })
    );
  };

  const formatLocale = document.documentElement.lang || LANGUAGE;

  const formatNumber = (value, digits) => {
    try {
      return new Intl.NumberFormat(formatLocale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      }).format(value);
    } catch (error) {
      return value.toFixed(digits);
    }
  };

  const formatDate = (date) => {
    try {
      return new Intl.DateTimeFormat(formatLocale, { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
    } catch (error) {
      return date.toISOString().slice(0, 10);
    }
  };

  /**
   * Redraws et-cart-page-rating-boxes to a rating rounded to the nearest half, the way the snippet draws it: one
   * rect per box (filled or not) and, for a half box, a second 7.5-wide filled rect before the box's star.
   */
  const drawBoxes = (svg, value) => {
    if (!svg) return;
    const rating = Math.min(5, Math.max(0, Math.round(Number(value) * 2) / 2));
    if (Number.isNaN(rating)) return;

    const filled = svg.querySelector(`.${FILLED_CLASS}`);
    const fillStyle = filled ? filled.getAttribute('style') : null;
    let rects = [];
    let index = 0;

    Array.from(svg.children).forEach((node) => {
      if (node.tagName.toLowerCase() === 'rect') {
        rects.push(node);
        return;
      }
      if (!node.classList.contains(STAR_CLASS) || !rects.length) return;

      const [box, ...extra] = rects;
      extra.forEach((rect) => rect.remove());
      rects = [];

      const isFilled = rating >= index + 1;
      box.setAttribute('width', '15');
      box.classList.toggle(FILLED_CLASS, isFilled);
      if (isFilled && fillStyle) box.setAttribute('style', fillStyle);
      else box.removeAttribute('style');

      if (!isFilled && rating >= index + 0.5) {
        const half = box.cloneNode(false);
        half.setAttribute('width', '7.5');
        half.classList.add(FILLED_CLASS);
        if (fillStyle) half.setAttribute('style', fillStyle);
        node.before(half);
      }
      index += 1;
    });
  };

  const valueText = (key, data) => {
    switch (key) {
      case 'score':
        return Number.isFinite(data.score) ? formatNumber(data.score, 1) : null;
      case 'stars':
        return Number.isFinite(data.stars) ? formatNumber(data.stars, 1) : null;
      case 'reviews':
        return Number.isFinite(data.reviews) ? formatNumber(data.reviews, 0) : null;
      case 'label':
        return data.label || null;
      default:
        return null;
    }
  };

  const HOOKS = '[data-et-tp], [data-et-tp-label], [data-et-tp-stars]';
  const PLACEHOLDER = /\[(score|stars|reviews|label)\]/g;

  const hydrate = (scope, data) => {
    scope.querySelectorAll('[data-et-tp]').forEach((element) => {
      const text = valueText(element.dataset.etTp, data);
      if (text !== null && element.textContent !== text) element.textContent = text;
    });

    scope.querySelectorAll('[data-et-tp-label]').forEach((element) => {
      let complete = true;
      const label = element.dataset.etTpLabel.replace(PLACEHOLDER, (match, key) => {
        const text = valueText(key, data);
        if (text === null) complete = false;
        return text === null ? match : text;
      });
      if (complete) element.setAttribute('aria-label', label);
    });

    if (!LIVE_STARS || !Number.isFinite(data.stars)) return;
    scope.querySelectorAll('[data-et-tp-stars]').forEach((host) => {
      const svg = host.matches('svg') ? host : host.querySelector('svg.et-cart-page-rating-boxes');
      drawBoxes(svg, data.stars);
    });
  };

  const hasHooks = (scope) => Boolean(scope.querySelector(HOOKS));

  const run = (scope) => {
    if (!hasHooks(scope)) return;
    summary()
      .then((data) => hydrate(scope, data))
      .catch(() => {
        // Keep the server-rendered fallback values.
      });
  };

  window.etTrustpilot = { reviews, summary, drawBoxes, formatDate, formatNumber, locale: LOCALE, language: LANGUAGE };

  // Deferred section scripts (e.g. the reviews carousel) start their reviews request before DOMContentLoaded, so the
  // summary waits for it and the page makes one request instead of two. This script is deferred too, and deferred
  // scripts run while readyState is already "interactive", so readyState cannot tell whether DOMContentLoaded is still
  // to come: listen for it, with load as the backstop for a late injection.
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    run(document);
  };
  if (document.readyState === 'complete') {
    start();
  } else {
    document.addEventListener('DOMContentLoaded', start, { once: true });
    window.addEventListener('load', start, { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) run(event.target);
  });

  // Late markup: the cart drawer re-renders its content, quick views and other fetched HTML land after load.
  if (typeof MutationObserver === 'function') {
    let queued = false;
    new MutationObserver((mutations) => {
      if (queued) return;
      const added = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) => node instanceof Element && (node.matches(HOOKS) || hasHooks(node))
        )
      );
      if (!added) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        run(document);
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
